import Feature from "ol/Feature";
import Style from "ol/style/Style";
import CircleStyle from "ol/style/Circle";
import Stroke from "ol/style/Stroke";
import Polygon from "ol/geom/Polygon";
import GeoJSON from "ol/format/GeoJSON";

import { buffer } from "@turf/turf";
import { AddRangeBoundVolume, RemoveRangeBoundVolume } from "./assetUtilTest";
import { allModels, ConvertGeometryToGeoJson, FILL_BLUE, GetFeature, GetSource, STROKE_WHITE, STYLE_VOLUME_MONITOR } from "../map/mapUtil";
import { Point } from "ol/geom";
import { Fill, Icon, Text } from "ol/style";

const GeoJson = new GeoJSON();

// Creates a new entity feature using ASD.
export function CreateEntity(entityData) {
    let imageSrc = `../static/2d-models/shapes/${entityData.model}.png`;
    if (entityData.model === "icon") {
        imageSrc = allModels.get(0);
        if (entityData.sourceType === "TELEMETRY") {
            imageSrc = allModels.get(8);
        } else if (entityData.emitterType && allModels.has(entityData.emitterType)) {
            imageSrc = allModels.get(entityData.emitterType);
        }
    } else {
        entityData.size *= 0.75;
    }

    const entityFeature = new Feature(new Point(entityData.coordinates));
    entityFeature.setId(entityData.asd.id);
    entityFeature.setProperties({
        name: entityData.asd.label,
        asd: entityData.asd,
        update_timestamp: entityData.asd.timestamp,
        style: new Style({
            image: new Icon({
                src: imageSrc,
                color: entityData.color,
                scale: entityData.size,
                rotation: entityData.rotation
            })
        })
    });
    entityFeature.setStyle(new Style(null));
    return entityFeature;
}

// Creates a new asset feature using the asset structure.
export function CreateAsset(asset) {
    const assetGeometry = GeoJson.readGeometry(asset.geometry);
    const assetFeature = new Feature(assetGeometry);
    assetFeature.setId(asset.uuid);
    assetFeature.setProperties({ name: asset.name, asset: asset });
    assetFeature.setStyle(
        new Style({
            fill: FILL_BLUE,
            stroke: STROKE_WHITE,
            text: new Text({
                text: asset.name,
                font: "bold 16px sans-serif",
                fill: new Fill({ color: "#FFFFFF" }),
                stroke: new Stroke({ color: "#000000", width: 3 }),
                textAlign: "center",
                textBaseline: "middle",
                offsetX: 0,
                offsetY: 0
            })
        })
    );
    return assetFeature;
}

export function StartAssetMonitoring(map, assetUUID) {
    if (!map || !assetUUID) {
        return null;
    }

    // Invalid asset -- nothing to monitor.
    const asset = GetFeature(map, "assets", assetUUID);
    if (!asset) {
        return null;
    }

    if (asset && !asset.get("asset").active_monitoring) {
        asset.get("asset").active_monitoring = true;
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(asset.get("asset"))
        };
        fetch("/api/assets/insertupdate", requestOptions).then((response) => (response.ok ? response.json() : Promise.reject(response)));
    }

    const geoJson = ConvertGeometryToGeoJson(asset.getGeometry());
    const buffered = buffer(geoJson, asset.get("asset").buffer_m, { units: "meters" });
    const assetMonitor = new Feature(new Polygon(buffered.geometry.coordinates));
    assetMonitor.setId(`${assetUUID}-monitor`);
    assetMonitor.setProperties({ assetFeature: asset, cuas: [], highAlerts: 0, mediumAlerts: 0, noRiskAlerts: 0 });
    assetMonitor.setStyle(STYLE_VOLUME_MONITOR);

    const assetMonitorSource = GetSource(map, "assetMonitors");
    assetMonitorSource.addFeature(assetMonitor);
    AddRangeBoundVolume(map, asset);
    return assetMonitor;
}

export async function StopAssetMonitoring(map, assetUUID) {
    if (!map || !assetUUID) {
        return null;
    }

    const assetMonitorSource = GetSource(map, "assetMonitors");
    const assetMonitor = assetMonitorSource.getFeatureById(`${assetUUID}-monitor`);
    if (!assetMonitor) {
        return null;
    }

    const asset = GetFeature(map, "assets", assetUUID);
    if (asset && asset.get("asset").active_monitoring) {
        asset.get("asset").active_monitoring = false;
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(asset.get("asset"))
        };
        await fetch("/api/assets/insertupdate", requestOptions).then((response) => (response.ok ? response.json() : Promise.reject(response)));
    }

    for (const cuas of assetMonitor.get("cuas")) {
        DeleteCounterUasFromAssetMonitor(cuas, assetMonitor);
    }

    assetMonitor.set("deleted", true);
    assetMonitorSource.removeFeature(assetMonitor);
    RemoveRangeBoundVolume(map, assetUUID);
    return assetMonitor;
}

export function DeleteCounterUas(cuasFeature) {
    if (!cuasFeature || typeof cuasFeature.get !== "function" || !cuasFeature.get("cuas") || !cuasFeature.get("monitors")) {
        return;
    }
    for (const assetMonitor of cuasFeature.get("monitors")) {
        DeleteCounterUasFromAssetMonitor(cuasFeature, assetMonitor);
    }
}

export function DeleteCounterUasFromAssetMonitor(cuasFeature, assetMonitor) {
    if (!cuasFeature || typeof cuasFeature.get !== "function" || !cuasFeature.get("cuas")) {
        return;
    }

    const assetMonitors = cuasFeature.get("monitors") || [];
    if (!assetMonitors.includes(assetMonitor)) {
        return;
    }

    const filteredMonitors = assetMonitors.filter((m) => m !== assetMonitor);
    cuasFeature.set("monitors", filteredMonitors);

    // The counter UAS isn't being monitored anymore -- Make it invisible.
    if (filteredMonitors.length === 0) {
        cuasFeature.set("cuas", false);
        cuasFeature.setStyle(new Style(null));
    }

    // Update the highest alert for the counter UAS.
    DisplayHighestAlert(cuasFeature);

    // Asset Monitor is already deleted.
    if (assetMonitor.get("deleted")) {
        return;
    }

    // Remove the entity from the monitor so it won't show up in info card.
    let counterUAS = assetMonitor.get("cuas") || [];
    counterUAS = counterUAS.filter((cuas) => cuas.getId() !== cuasFeature.getId());
    assetMonitor.set("cuas", counterUAS);

    // Remove the alert from the monitor.
    const alert = assetMonitor.get("alerts")[cuasFeature.getId()];
    assetMonitor.set("highAlerts", assetMonitor.get("highAlerts") - (alert.risk_val === "HIGH" ? 1 : 0));
    assetMonitor.set("mediumAlerts", assetMonitor.get("mediumAlerts") - (alert.risk_val === "MEDIUM" ? 1 : 0));
    assetMonitor.set("noRiskAlerts", assetMonitor.get("noRiskAlerts") - (alert.risk_val === "NO_RISK" ? 1 : 0));
}

export function DisplayHighestAlert(cuasFeature) {
    if (!cuasFeature || typeof cuasFeature.get !== "function" || !cuasFeature.get("cuas") || !cuasFeature.get("monitors")) {
        return;
    }

    const alertPriority = {
        HIGH: 3,
        MEDIUM: 2,
        NO_RISK: 1
    };

    let highestAlert = "NO_RISK";
    for (const assetMonitor of cuasFeature.get("monitors")) {
        const alert = assetMonitor.get("alerts")[cuasFeature.getId()];
        if (alertPriority[alert.risk_val] > alertPriority[highestAlert]) {
            highestAlert = alert.risk_val;
        }
    }

    cuasFeature.set(
        "cuasStyle",
        new Style({
            image: new CircleStyle({
                radius: 25,
                stroke: new Stroke({
                    color: highestAlert === "HIGH" ? "red" : highestAlert === "MEDIUM" ? "yellow" : "green",
                    width: 2
                })
            })
        })
    );
    cuasFeature.setStyle([cuasFeature.get("style"), cuasFeature.get("cuasStyle")]);
}
